import { Container, Theme, createTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { UxBeautyTrendsAndScienceImage } from 'components/constants/Constants';
import { NotFoundPage } from 'components/pages/errors/NotFoundPage';
import { LoginPage } from 'components/pages/Login/LoginPage';
import SearchWrapper from 'components/pages/Search/SearchWrapper';
import UserProfile from 'components/pages/user/UserProfile/UserProfile';
import { lazy } from 'react';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter
} from 'react-router-dom';
import PrivateRouteNew from 'routes/PrivateRouteNew';
import { ThemeProvider } from 'theme/Theme';
import { ProductType } from 'types/Products';
import { CartProvider } from '../contexts/cart/CartContext';
import { GenericLayoutRoot } from '../../generic/layout/GenericLayoutRoot';
import { ManagementDashboard } from '../pages/AdminDashboard/ManagementDashboard';
import Homepage from '../pages/Homepage/Homepage';
import { LayoutRedirector } from '../pages/LayoutRedirector';
import LegalNotice from '../pages/LegalNotice/LegalNotice';
import MeetUsPage from '../pages/MeetUs/MeetUsPage';
import PressRelease from '../pages/PressRelease/PressRelease';
import { SampleCart } from '../pages/SampleCart/SampleCartPage';
import { TrendsAndScience } from '../pages/TrendsAndScience/TrendsAndScience';
import ProductPage from '../ProductPage/ProductPage';
import { UXB_SPECIAL_COLORS, uxBeautyTheme } from '../Theme';
import { UXB_ROUTE_NAMES } from '../UXBRouteNames';
import { CookiesProvider } from '../cookiesfooter/CookiesProvider';
import { Footer } from '../footer/Footer';
import { CookiesFooter } from '../cookiesfooter/CookiesFooter';
import { EventSignupDialog } from '../pages/EventSignup/EventSignupDialog';
import { NavigationBar } from '../navigation/NavigationBar';
const UXBBackgroundWrapper = lazy(() =>
  import('../pages/common/UXBBackgroundWrapper').then(
    ({ UXBBackgroundWrapper }) => ({
      default: UXBBackgroundWrapper
    })
  )
);
const WebinarPage = lazy(() => import('../pages/Webinars/WebinarPage'));

const ProductView = lazy(() => import('components/pages/products/ProductView'));
const ProductsEdit = lazy(
  () => import('components/pages/product/ProductsEdit')
);
const RegistrationPage = lazy(
  () => import('../../../components/pages/register/Register/RegistrationPage')
);
const ActivationPage = lazy(() => import('../pages/Activation/ActivationPage'));
const UserAccessPage = lazy(() => import('../pages/UserAccess/UserAccessPage'));
const ContactsPage = lazy(() => import('../pages/Contacts/ContactsPage'));
const ContentPage = lazy(() => import('../pages/Content/ContentPage'));

const router = createBrowserRouter([
  {
    path: '/en/product/128-s3d-blissful-vibes.html',
    element: (
      <Navigate to="/concepts/permalink/f--personal-care--givaudan--s3d-blissful-vibes" />
    )
  },
  {
    path: '/en/product/:productName.html',
    element: <LayoutRedirector />
  },
  {
    path: '/',
    element: (
      <GenericLayoutRoot
        defaultColor={UXB_SPECIAL_COLORS.ingredients}
        NavigationBarComponent={NavigationBar}
        FooterComponent={Footer}
        FooterDrawerComponent={CookiesFooter}
        EventSignupDialogComponent={EventSignupDialog}
      />
    ),
    children: [
      {
        path: '/',
        element: <Homepage />,
        handle: {
          showFooter: true,
          customBackground: 'white'
        }
      },
      {
        path: '/login',
        element: <LoginPage />
      },

      {
        path: '/ingredients/search',
        element: (
          <Container sx={{ mt: 2 }}>
            <SearchWrapper
              key={ProductType.INGREDIENTS}
              target={ProductType.INGREDIENTS}
            />
          </Container>
        ),
        handle: {
          customBackground: UXB_SPECIAL_COLORS.ingredients
        }
      },
      {
        path: '/concepts/search',
        element: (
          <Container sx={{ mt: 2 }}>
            <SearchWrapper
              key={ProductType.FORMULATIONS}
              target={ProductType.FORMULATIONS}
            />
          </Container>
        ),
        handle: {
          customBackground: UXB_SPECIAL_COLORS.concepts
        }
      },
      {
        path: '/press-release',
        element: (
          <Container>
            <PressRelease />
          </Container>
        ),
        handle: {
          showFooter: true,
          customBackground: UXB_SPECIAL_COLORS.pressRelease
        }
      },
      {
        path: '/meet-us',
        element: <MeetUsPage />,
        handle: {
          showFooter: true,
          hideInstaFooter: true,
          customBackground: 'white'
        }
      },
      {
        path: '/webinar/:id',
        element: <WebinarPage />,
        handle: {
          showFooter: true,
          hideInstaFooter: true,
          customBackground: UXB_SPECIAL_COLORS.pressRelease
        }
      },

      {
        path: '/ingredients/permalink/:productKey',
        element: (
          <Container>
            <ProductPage />
          </Container>
        ),
        handle: {
          showFooter: true,
          customBackground: UXB_SPECIAL_COLORS.ingredients
        }
      },
      {
        path: '/concepts/permalink/:productKey',
        element: (
          <Container>
            <ProductPage />
          </Container>
        ),
        handle: {
          showFooter: true,
          customBackground: UXB_SPECIAL_COLORS.concepts
        }
      },
      {
        path: '/legal-notice',
        element: (
          <Container>
            <LegalNotice />
          </Container>
        ),
        handle: {
          showFooter: true,
          customBackground: 'white',
          hideEventSignupDialog: true
        }
      },
      {
        path: '/trends-and-science',
        element: <PrivateRouteNew component={<TrendsAndScience />} />,
        handle: {
          showFooter: true,
          hideInstaFooter: true,
          customBackgroundImage: UxBeautyTrendsAndScienceImage
        }
      },
      {
        path: UXB_ROUTE_NAMES.userProfile,
        element: (
          <Container sx={{ my: 2 }}>
            <UserProfile />
          </Container>
        )
      },
      {
        path: UXB_ROUTE_NAMES.cart,
        element: (
          <Container sx={{ my: 2 }}>
            <SampleCart />
          </Container>
        ),
        handle: {
          hideInstaFooter: true,
          showFooter: true,
          customBackground: UXB_SPECIAL_COLORS.sampleCart
        }
      },
      {
        path: `/admin`,
        element: (
          <PrivateRouteNew
            component={
              <Container>
                <ManagementDashboard />
              </Container>
            }
          />
        )
      },
      {
        path: `/admin/user`,
        element: <PrivateRouteNew component={<UserAccessPage />} />
      },
      {
        path: `/admin/contacts`,
        element: <PrivateRouteNew component={<ContactsPage />} />
      },
      {
        path: `/admin/content`,
        element: <PrivateRouteNew component={<ContentPage />} />
      },
      {
        path: `/admin/products/ingredients`,
        element: (
          <PrivateRouteNew
            component={
              <ProductsEdit
                productType={ProductType.INGREDIENTS}
                key={ProductType.INGREDIENTS}
              />
            }
          />
        ),
        handle: {
          customBackground: UXB_SPECIAL_COLORS.ingredients
        }
      },
      {
        path: `/admin/products/formulations`,
        element: (
          <PrivateRouteNew
            component={
              <ProductsEdit
                productType={ProductType.FORMULATIONS}
                key={ProductType.FORMULATIONS}
              />
            }
          />
        ),
        handle: {
          customBackground: UXB_SPECIAL_COLORS.concepts
        }
      },
      ...[ProductType.INGREDIENTS, ProductType.FORMULATIONS].map(type => ({
        path: `/admin/product/${type.toLowerCase()}/:company/:segment/editor/new`,
        element: (
          <PrivateRouteNew
            component={<ProductView productType={type} editing={false} />}
          />
        ),
        handle: {
          customBackground:
            type === ProductType.INGREDIENTS
              ? UXB_SPECIAL_COLORS.ingredients
              : UXB_SPECIAL_COLORS.concepts
        }
      })),
      ...[ProductType.INGREDIENTS, ProductType.FORMULATIONS].map(type => ({
        path: `/admin/product/${type.toLowerCase()}/:company/:segment/editor/new/duplicate/:productToDuplicate`,
        element: (
          <PrivateRouteNew
            component={<ProductView productType={type} editing={false} />}
          />
        ),
        handle: {
          customBackground:
            type === ProductType.INGREDIENTS
              ? UXB_SPECIAL_COLORS.ingredients
              : UXB_SPECIAL_COLORS.concepts
        }
      })),

      ...[ProductType.INGREDIENTS, ProductType.FORMULATIONS].map(type => ({
        path: `/admin/product/${type.toLowerCase()}/:company/:segment/:id`,
        element: (
          <PrivateRouteNew
            component={<ProductView productType={type} editing />}
          />
        ),
        handle: {
          customBackground:
            type === ProductType.INGREDIENTS
              ? UXB_SPECIAL_COLORS.ingredients
              : UXB_SPECIAL_COLORS.concepts
        }
      })),
      {
        path: '/login',
        element: <LoginPage />
      },
      {
        path: '*', // any unknown route
        element: <Homepage />,
        handle: {
          showFooter: true
        }
      },
      {
        path: '/notFound',
        element: <NotFoundPage />,
        handle: {
          showFooter: false,
          hideInstaFooter: false
        }
      }
    ]
  },
  {
    path: '/register',
    element: (
      <UXBBackgroundWrapper>
        <RegistrationPage />
      </UXBBackgroundWrapper>
    ),
    handle: {
      showFooter: true,
      hideInstaFooter: true
    }
  },
  {
    path: '/activate',
    element: <ActivationPage />,
    handle: {
      showFooter: true,
      hideInstaFooter: true
    }
  }
]);

const Layout = () => (
  <ThemeProvider
    theme={(theme: Theme) => createTheme(deepmerge(theme, uxBeautyTheme))}
  >
    <CartProvider>
      <CookiesProvider>
        <RouterProvider router={router} />
      </CookiesProvider>
    </CartProvider>
  </ThemeProvider>
);

export default Layout;
